import type {
  CommentActionRequest,
  CommentResponse,
  DeleteCommentsCommentIdParams,
  ExportUnifiedCommentsCSVRequest,
} from '@dashhudson/api-client/community/model';

import {
  getDeleteAiCategoriesAiCategoryIdUrl,
  getDeleteCommentsCommentIdUrl,
  getDeleteTagsTagIdUrl,
  getGetCommentsCommentIdUrl,
  getGetFacebookCommentsCommunityInteractionIdUrl,
  getGetInstagramCommentsCommunityInteractionIdUrl,
  getGetTiktokCommentsCommunityInteractionIdUrl,
  getGetTweetsCommunityInteractionIdUrl,
  getGetYoutubeCommentsCommunityInteractionIdUrl,
  getPatchCommunityInteractionsCommunityInteractionIdUrl,
  getPatchCommunityInteractionsUrl,
  getPostCommentsCommentIdActionsUrl,
  getPostCommentsCommentIdPrivateReplyUrl,
  getPostCommentsCsvUrl,
  getPostCommunityInteractionsBulkUpdateByMediaUrl,
  getPostCommunityInteractionsCsvUrl,
  getPostFacebookCommentsCsvUrl,
  getPostInstagramCommentsCsvUrl,
  getPostIntegrationsTicketsUrl,
  getPostTagsUrl,
  getPostTiktokCommentsCsvUrl,
  getPostTweetsCsvUrl,
  getPostYoutubeCommentsCsvUrl,
  getPutCommunityInteractionsCommunityInteractionIdTicketsSourceIdUrl,
} from '@dashhudson/api-client/community/client';
import merge from 'lodash/merge';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { getUrlPathByPlatform } from '@/app/community/utils/comments';
import { env } from '@/env';

export const http = createDashHttpClient(env.communityApiUrl, {
  camelizeKeys: true,
});

type CommunityId = number | string;
type Payload = object;
type Params = Record<string, unknown>;
type CsvArg = { socketId?: string; mediaId?: CommunityId; brandId?: CommunityId };
type IdArg = { id: CommunityId };

// Builders type ids as number; store/route ids arrive as either, and the path renders both.
const pathId = (id: CommunityId) => id as number;

export function saveCommunityInteractionTicket(
  {
    communityInteractionId,
    source,
    sourceId,
    sourceCreatedAt,
    sourceUpdatedAt,
    label,
    status,
    messageId,
  }: {
    communityInteractionId: CommunityId;
    source?: string;
    sourceId: string;
    sourceCreatedAt?: string | null;
    sourceUpdatedAt?: string | null;
    label?: string | null;
    status?: string | null;
    messageId?: CommunityId | null;
  },
  config: HttpRequestConfig = {},
) {
  return http.put(
    getPutCommunityInteractionsCommunityInteractionIdTicketsSourceIdUrl(
      pathId(communityInteractionId),
      sourceId,
    ),
    {
      source,
      sourceId,
      sourceCreatedAt,
      sourceUpdatedAt,
      label,
      status,
      messageId,
    },
    config,
  );
}

export function getCommentInteractionList(
  {
    platform,
    brandId,
    mediaId,
    limit = 12,
  }: { platform: string; brandId?: CommunityId; mediaId?: CommunityId; limit?: number },
  config: HttpRequestConfig = {},
) {
  const params = {
    brandId,
    mediaId,
    limit,
  };
  const url = `/${getUrlPathByPlatform(platform)}`;
  return http.get(
    url,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getCommentInteractionDetail(
  {
    platform,
    communityInteractionId,
    returnParent,
  }: { platform: string; communityInteractionId: CommunityId; returnParent?: boolean },
  config: HttpRequestConfig = {},
) {
  const params = {
    returnParent,
  };
  return http.get(
    `/${getUrlPathByPlatform(platform)}/${communityInteractionId}`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getFacebookComment({ id }: IdArg, config: HttpRequestConfig = {}) {
  return http.get(getGetFacebookCommentsCommunityInteractionIdUrl(pathId(id)), config);
}

export function getInstagramComment({ id }: IdArg, config: HttpRequestConfig = {}) {
  return http.get(getGetInstagramCommentsCommunityInteractionIdUrl(pathId(id)), config);
}

export function getComment({ id }: IdArg, config: HttpRequestConfig = {}) {
  return http.get(getGetCommentsCommentIdUrl(pathId(id)), config);
}

export function getTikTokComment({ id }: IdArg, config: HttpRequestConfig = {}) {
  return http.get(getGetTiktokCommentsCommunityInteractionIdUrl(pathId(id)), config);
}

export function getTwitterComment({ id }: IdArg, config: HttpRequestConfig = {}) {
  return http.get(getGetTweetsCommunityInteractionIdUrl(pathId(id)), config);
}

export function getYoutubeComment({ id }: IdArg, config: HttpRequestConfig = {}) {
  return http.get(getGetYoutubeCommentsCommunityInteractionIdUrl(pathId(id)), config);
}

export function performCommentAction(
  commentId: CommunityId,
  payload: CommentActionRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<CommentResponse>(
    getPostCommentsCommentIdActionsUrl(pathId(commentId)),
    payload,
    config,
  );
}

export function deleteCommentInteraction(
  commentId: CommunityId,
  params: DeleteCommentsCommentIdParams,
  config: HttpRequestConfig = {},
) {
  return http.delete(getDeleteCommentsCommentIdUrl(pathId(commentId), params), config);
}

export function getCommentsCsv(
  payload: ExportUnifiedCommentsCSVRequest,
  config: HttpRequestConfig = {},
) {
  return http.post(getPostCommentsCsvUrl(), payload, config);
}

export function getFacebookCommentsCsv(
  { socketId, mediaId, brandId }: CsvArg,
  config: HttpRequestConfig = {},
) {
  return http.post(getPostFacebookCommentsCsvUrl(), { socketId, mediaId, brandId }, config);
}

export function getInstagramCommentsCsv(
  { socketId, mediaId, brandId }: CsvArg,
  config: HttpRequestConfig = {},
) {
  return http.post(getPostInstagramCommentsCsvUrl(), { socketId, mediaId, brandId }, config);
}

export function getTikTokCommentsCsv(
  { socketId, mediaId, brandId }: CsvArg,
  config: HttpRequestConfig = {},
) {
  return http.post(getPostTiktokCommentsCsvUrl(), { socketId, mediaId, brandId }, config);
}

export function getTwitterCommentsCsv(
  { socketId, mediaId, brandId }: CsvArg,
  config: HttpRequestConfig = {},
) {
  return http.post(getPostTweetsCsvUrl(), { socketId, mediaId, brandId }, config);
}

export function getYoutubeCommentsCsv(
  { socketId, mediaId, brandId }: CsvArg,
  config: HttpRequestConfig = {},
) {
  return http.post(getPostYoutubeCommentsCsvUrl(), { socketId, mediaId, brandId }, config);
}

export function getAllMessagesCsv(payload: Payload, config: HttpRequestConfig = {}) {
  return http.post(getPostCommunityInteractionsCsvUrl(), payload, config);
}

/*
   Interaction rules endpoints
*/
export async function getInteractionRulesForBrands(
  brandIds: CommunityId[],
  config: HttpRequestConfig = {},
) {
  const url = `/interaction_rules`;
  const params = { brandIds: brandIds.join(',') };
  return http.get(url, merge({ params }, config));
}
export async function createInteractionRule(
  brandId: CommunityId,
  payload: Payload,
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/interaction_rules`;
  return http.post(url, payload, config);
}
export async function updateInteractionRule(
  brandId: CommunityId,
  ruleId: CommunityId,
  payload: Payload,
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/interaction_rules/${ruleId}`;

  return http.patch(url, payload, config);
}
export async function deleteInteractionRule(
  brandId: CommunityId,
  ruleId: CommunityId,
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/interaction_rules/${ruleId}`;
  return http.delete(url, config);
}
export async function deleteInteractionRulesHistory(
  brandId: CommunityId,
  ruleExecutionHistoryId: CommunityId,
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/interaction_rules_history/${ruleExecutionHistoryId}`;
  return http.delete(url, config);
}

export async function getRuleFilterMatchCount(
  brandId: CommunityId,
  ruleFilters: unknown,
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/interaction_rules/match_count`;
  return http.post(url, { ruleFilters }, config);
}

export async function getInteractionRulesPreview(
  brandId: CommunityId,
  filters: unknown,
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/interaction_rules/preview`;
  return http.post(url, { filters }, config);
}

/*
   AI Category API
*/
export async function getAiCategoriesForBrands(
  brandIds: CommunityId[] | CommunityId,
  config: HttpRequestConfig = {},
) {
  const params = { brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds };
  return http.get('/ai_categories', merge({ params }, config));
}

export async function createAiCategory(
  { brandId, name, prompt }: { brandId: CommunityId; name?: string; prompt?: string },
  config: HttpRequestConfig = {},
) {
  const url = `/brands/${brandId}/ai_category`;
  const payload = { name, prompt };
  return http.post(url, payload, config);
}

export async function deleteAiCategory(aiCategoryId: CommunityId, config: HttpRequestConfig = {}) {
  return http.delete(getDeleteAiCategoriesAiCategoryIdUrl(pathId(aiCategoryId)), config);
}

// Platforms served only by the unified `/comments` endpoints, with no per-platform route.
const UNIFIED_COMMENTS_PLATFORMS = new Set(['linkedin']);

export default {
  async bulkUpdateCommunityInteractionsByMedia(
    {
      brandId,
      mediaId,
      isArchived,
      isRead,
    }: { brandId: CommunityId; mediaId: CommunityId; isArchived?: boolean; isRead?: boolean },
    config: HttpRequestConfig = {},
  ) {
    // JSON drops the undefined flag the caller left out, so only the one it set is sent.
    const data = { brand_id: brandId, media_id: mediaId, is_archived: isArchived, is_read: isRead };
    return http.post(getPostCommunityInteractionsBulkUpdateByMediaUrl(), data, config);
  },
  async updatePostEnabled(
    {
      brandId,
      mediaId,
      commentDisabled,
    }: { brandId: CommunityId; mediaId: CommunityId; commentDisabled?: boolean },
    config: HttpRequestConfig = {},
  ) {
    const url = `/posts/${brandId}/${mediaId}`;
    return http.patch(url, { commentDisabled }, config);
  },
  async createTweet(
    {
      brandId,
      parentTweetId,
      text,
      isQuote,
    }: {
      brandId: CommunityId;
      parentTweetId?: CommunityId | null;
      text?: string;
      isQuote?: boolean;
    },
    config: HttpRequestConfig = {},
  ) {
    const url = `/tweets`;
    return http.post(url, { brandId, parentTweetId, text, isQuote }, config);
  },
  async patchTweet(
    {
      communityInteractionId,
      isHidden = null,
      isLiked = null,
      isRetweeted = null,
    }: {
      communityInteractionId: CommunityId;
      isHidden?: boolean | null;
      isLiked?: boolean | null;
      isRetweeted?: boolean | null;
    },
    config: HttpRequestConfig = {},
  ) {
    const url = `/tweets/${communityInteractionId}`;
    const payload = { isHidden, isRetweeted, isLiked };
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== null),
    );
    return http.patch(url, filteredPayload, config);
  },
  async getPlatformAccount(
    { platform, brandId }: { platform: string; brandId: CommunityId },
    config: HttpRequestConfig = {},
  ) {
    const url = `/conversations/${platform}/accounts/${brandId}`;
    return http.get(url, config);
  },

  //
  // PRESET REPLY API
  //
  async getPresetReplyList(
    { brandId, type }: { brandId: CommunityId; type?: string },
    config: HttpRequestConfig = {},
  ) {
    const url = `/preset_replies`;
    const params = { brandId, type };
    return http.get(url, merge({ params }, config));
  },
  async createPresetReply({ payload }: { payload: Payload }, config: HttpRequestConfig = {}) {
    const url = `/preset_replies`;
    return http.post(url, payload, config);
  },
  async updatePresetReply(
    { replyId, payload }: { replyId: CommunityId; payload: Payload },
    config: HttpRequestConfig = {},
  ) {
    const url = `/preset_replies/${replyId}`;
    return http.patch(url, payload, config);
  },
  async deletePresetReply(replyId: CommunityId, config: HttpRequestConfig = {}) {
    const url = `/preset_replies/${replyId}`;
    return http.delete(url, config);
  },
  async getUnreadCommunityInteractionCountsV2(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/community_interactions/unread_interaction_counts`;
    return http.post(url, payload, { ...config, camelizeKeys: false });
  },
  async getCommunityInteractionCounts(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/community_interactions/counts`;
    return http.post(url, payload, { ...config, camelizeKeys: false });
  },

  //
  // COMMENT API
  //
  async getCommentList(
    {
      next,
      brandId,
      mediaId,
      limit,
      platform,
    }: {
      next?: string | null;
      brandId?: CommunityId;
      mediaId?: CommunityId;
      limit?: number;
      platform: string;
    },
    config: HttpRequestConfig = {},
  ) {
    const path = UNIFIED_COMMENTS_PLATFORMS.has(platform)
      ? 'comments'
      : getUrlPathByPlatform(platform);
    const url = next || `/${path}?brand_id=${brandId}&media_id=${mediaId}&limit=${limit}`;
    return http.get(url, config);
  },
  async getCommentDetail(
    id: CommunityId,
    returnParent: boolean | undefined,
    platform: string,
    config: HttpRequestConfig = {},
  ) {
    if (UNIFIED_COMMENTS_PLATFORMS.has(platform)) {
      // The unified endpoint has no `return_parent`; fetch the parent by its ID instead.
      const response = await http.get(`/comments/${id}`, config);
      if (returnParent && response.data.parentId != null) {
        return http.get(`/comments/${response.data.parentId}`, config);
      }
      return response;
    }
    const url = returnParent
      ? `/${getUrlPathByPlatform(platform)}/${id}?return_parent=${returnParent}`
      : `/${getUrlPathByPlatform(platform)}/${id}`;
    return http.get(url, config);
  },
  async updateCommentRequest(
    {
      commentId,
      platform,
      payload,
    }: { commentId: CommunityId; platform: string; payload: Payload },
    config: HttpRequestConfig = {},
  ) {
    const url = `/${getUrlPathByPlatform(platform)}/${commentId}`;
    return http.patch(url, payload, config);
  },
  async deleteComment(
    commentId: CommunityId,
    platform: string,
    softDelete?: boolean,
    config: HttpRequestConfig = {},
  ) {
    const url = `/${getUrlPathByPlatform(platform)}/${commentId}${
      softDelete ? '?soft_delete_only=true' : ''
    }`;
    return http.delete(url, config);
  },
  async createComment(platform: string, payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/${getUrlPathByPlatform(platform)}`;
    return http.post(url, payload, config);
  },

  /* COMMUNITY OVERVIEW V2 APIS */
  async getOverviewStatsV2(params: Params, config: HttpRequestConfig = {}) {
    const url = `/overview_stats`;
    return http.get(url, { ...config, params });
  },
  async getAggregateSentimentByPlatformV2(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/sentiment_distribution_stats`;
    return http.post(url, payload, config);
  },
  async getVolumeTimeSeriesStatsV2(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/volume_time_series`;
    return http.post(url, payload, config);
  },
  async getSentimentTimeSeriesV2(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/sentiment_time_series`;
    return http.post(url, payload, config);
  },

  async getWordCloudTopKeywordsV2(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/top_keywords`;
    return http.post(url, payload, config);
  },

  /* COMMUNITY TEAM ACTIVITY */
  async getTeamActivities(payload: Payload, config: HttpRequestConfig = {}) {
    const url = `/team_activities`;
    return http.post(url, payload, { ...config, camelizeKeys: false });
  },

  //
  // COMMUNITY INTERACTIONS
  //
  async getCommunityInteractions(
    params?: Params & { next?: string | null },
    config: HttpRequestConfig = {},
  ) {
    const url = params?.next ?? '/community_interactions';
    return http.get(url, { ...config, params: { ...params, next: undefined } });
  },
  async updateCommunityInteraction(
    id: CommunityId,
    payload: Payload,
    config: HttpRequestConfig = {},
  ) {
    return http.patch(
      getPatchCommunityInteractionsCommunityInteractionIdUrl(pathId(id)),
      payload,
      config,
    );
  },
  async bulkUpdateCommunityInteractions(
    payload: Payload,
    params: Params = {},
    config: HttpRequestConfig = {},
  ) {
    return http.patch(getPatchCommunityInteractionsUrl(), payload, { ...config, params });
  },
  async getTags(params?: Params, config: HttpRequestConfig = {}) {
    return http.get('/tags', { ...config, params });
  },
  async getAllTags(brandIds: string, config: HttpRequestConfig = {}) {
    const tags = [];
    let offset = 0;
    let next: string | null = null;

    do {
      const response = await this.getTags({ brandIds, limit: 1000, offset }, config);
      tags.push(...response.data.data);
      next = response.data.paging?.next ?? null;
      if (next) {
        offset = Number(new URLSearchParams(next.split('?')[1]).get('offset'));
      }
    } while (next);

    return tags;
  },
  async createTag(payload: Payload, config: HttpRequestConfig = {}) {
    return http.post(getPostTagsUrl(), payload, config);
  },
  async deleteTag(tagId: CommunityId, config: HttpRequestConfig = {}) {
    return http.delete(getDeleteTagsTagIdUrl(pathId(tagId)), config);
  },

  async createAssignment(
    brandId: CommunityId,
    interactionId: CommunityId,
    payload: Payload,
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/community_interactions/${interactionId}/assignments`;
    return http.post(url, payload, config);
  },
  async getAssignees(
    {
      brandIds,
      sourceUpdatedAfter,
      sourceUpdatedBefore,
    }: {
      brandIds?: CommunityId[] | string;
      sourceUpdatedAfter?: string;
      sourceUpdatedBefore?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    return http.get('/assignees', {
      ...config,
      params: { brandIds, sourceUpdatedAfter, sourceUpdatedBefore },
    });
  },

  //
  // SALESFORCE INTEGRATION
  //

  async getCaseById(params: Params, config: HttpRequestConfig = {}) {
    return http.get('/integrations/tickets', {
      ...config,
      params,
    });
  },

  async createSalesforceCase(payload: Payload, config: HttpRequestConfig = {}) {
    return http.post(getPostIntegrationsTicketsUrl(), payload, config);
  },
  async sendPrivateReply(
    { commentId, text }: { commentId: CommunityId; text?: string },
    config: HttpRequestConfig = {},
  ) {
    return http.post(getPostCommentsCommentIdPrivateReplyUrl(pathId(commentId)), { text }, config);
  },
};
